import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { InspectionApi } from "@/lib/api";
import { workflowStatusConfig } from "@/config/cta-config";
import { NEUTRAL_BADGE, humanizeCode } from "@/config/projetos-config";

interface Props {
  vistorias: InspectionApi[];
}

export default function ProjectInspectionTable({ vistorias }: Props) {
  const navigate = useNavigate();

  // Contagem por status real, sem categorias inventadas.
  const porStatus = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const v of vistorias) acc[v.status] = (acc[v.status] ?? 0) + 1;
    return Object.entries(acc).sort((a, b) => b[1] - a[1]);
  }, [vistorias]);

  return (
    <div className="space-y-4">
      {porStatus.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {porStatus.map(([status, n]) => {
            const cfg = workflowStatusConfig[status];
            return (
              <span key={status} className={`badge ${cfg?.badge ?? NEUTRAL_BADGE}`}>
                {cfg?.label ?? humanizeCode(status)}: {n}
              </span>
            );
          })}
        </div>
      )}

      <div className="overflow-x-auto border border-background-200 rounded-lg">
        <table className="w-full">
          <thead>
            <tr className="border-b border-background-200 bg-background-50">
              <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Código CTA</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Espécie</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Data</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Tipo</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Técnico</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Status</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Ações</th>
            </tr>
          </thead>
          <tbody>
            {vistorias.map((v) => {
              const st = workflowStatusConfig[v.status];
              return (
                <tr key={v.id} className="border-b border-background-200 hover:bg-background-50 transition-colors">
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono text-foreground-800 whitespace-nowrap">{v.cta_code ?? "—"}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-foreground-700 whitespace-nowrap">{v.species_popular_name ?? "Não identificada"}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-foreground-700 whitespace-nowrap">
                      {v.inspection_date ? new Date(v.inspection_date).toLocaleDateString("pt-BR") : "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-foreground-600 bg-background-100 px-2 py-0.5 rounded-md whitespace-nowrap">
                      {humanizeCode(v.inspection_type)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-foreground-700 whitespace-nowrap">{v.technician_name ?? "—"}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`badge ${st?.badge ?? NEUTRAL_BADGE}`}>{st?.label ?? humanizeCode(v.status)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate(`/cta/${v.tree_id}`)}
                      className="w-7 h-7 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer"
                      title="Ver ficha do exemplar"
                    >
                      <i className="ri-eye-line text-xs"></i>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
